import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import api from '../utils/api';
import { bestMatch, groupByUser, DEFAULT_THRESHOLDS } from '../utils/faceMatch';
import { APP_NAME } from '../config/brand';

const SCAN_INTERVAL_MS = 1500;
const LOCAL_COOLDOWN_MS = 60 * 1000;
// After ANY successful mark, ignore the camera for a moment. One person
// walking away and the next stepping up takes longer than this; one face
// being re-read as somebody else takes less. (2026-09-02: two people marked
// 0.3s apart from a single face.)
const POST_MARK_QUIET_MS = 4 * 1000;
// The same person must win two scans in a row before we mark. A single frame
// with a half-turned head is exactly when a lookalike edges ahead.
const CONSECUTIVE_HITS = 2;
// SSD MobileNet is what enrollment used server-side; using the same detector
// here means the face is cropped and aligned the same way, so descriptors
// compare like-for-like. Tiny detector was faster but drifted.
const DETECTOR_OPTS = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.6 });

// Fullscreen entrance-tablet scanner. Auth comes from the operator's own
// logged-in session (guard/HR sign in on the tablet once) rather than a token
// compiled into the bundle — see kioskAuth in backend/src/routes/attendance.js.
export default function KioskAttendance() {
  const videoRef = useRef(null);
  const [status, setStatus] = useState('Loading face models…');
  const [detail, setDetail] = useState('');
  const [fatal, setFatal] = useState('');
  const [people, setPeople] = useState([]);
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const lastLocalMark = useRef({});
  const lastAnyMark = useRef(0);
  const streak = useRef({ userId: null, count: 0 });
  const scanning = useRef(false);

  const fetchDescriptors = useCallback(async () => {
    try {
      const { data } = await api.get('/attendance/kiosk/descriptors');
      // New shape: { thresholds, people:[{userId,name,descriptors:[[..]]}] }.
      // Older servers returned a flat array of {userId,name,descriptor}.
      const grouped = Array.isArray(data)
        ? groupByUser(data)
        : (data?.people || []).map(p => ({
          userId: p.userId, name: p.name,
          descriptors: (p.descriptors || []).map(d => new Float32Array(d)),
        }));
      if (data?.thresholds) setThresholds({ ...DEFAULT_THRESHOLDS, ...data.thresholds });
      setPeople(grouped);
      return grouped.length;
    } catch (e) {
      const code = e.response?.status;
      if (code === 401) setFatal(`Not signed in on this tablet. Please log in to ${APP_NAME} first, then reopen this page.`);
      else if (code === 403) setFatal('This account is not allowed to run the attendance kiosk. Sign in as the security guard or HR.');
      else setFatal(e.response?.data?.error || 'Could not load enrolled faces from the server.');
      return null;
    }
  }, []);

  useEffect(() => {
    let stream;
    let cancelled = false;
    (async () => {
      try {
        await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
        await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
        await faceapi.nets.faceRecognitionNet.loadFromUri('/models');
      } catch (e) {
        console.error('[kiosk] model load failed:', e);
        setFatal('Could not load the face-recognition models. Check the network and reload.');
        return;
      }
      if (cancelled) return;

      const count = await fetchDescriptors();
      if (cancelled || count === null) return;
      if (count === 0) {
        setFatal('No employee faces are enrolled yet. Enroll staff first, then reopen this scanner.');
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch (err) { console.error('[kiosk] play() failed:', err); }
        }
        setStatus('Scanning…');
      } catch (err) {
        console.error('[kiosk] getUserMedia failed:', err.name, err.message);
        setFatal(
          err.name === 'NotAllowedError'
            ? 'Camera permission denied. Allow camera for this site, then reload.'
            : err.name === 'NotFoundError'
            ? 'No camera found on this device.'
            : err.name === 'NotReadableError'
            ? 'Camera is in use by another app — close it and reload.'
            : `Camera error: ${err.name || err.message}`
        );
      }
    })();

    const refreshTimer = setInterval(fetchDescriptors, 15 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(refreshTimer);
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, [fetchDescriptors]);

  useEffect(() => {
    if (fatal || people.length === 0) return undefined;
    const scanTimer = setInterval(async () => {
      // Scans are serialised. Detection takes longer than the interval on a
      // slow tablet, and two overlapping scans marking different people is
      // how the double-mark happened.
      if (scanning.current) return;
      if (!videoRef.current || !videoRef.current.videoWidth) return;
      if (Date.now() - lastAnyMark.current < POST_MARK_QUIET_MS) return;
      scanning.current = true;
      try {
        let detection;
        try {
          detection = await faceapi
            .detectSingleFace(videoRef.current, DETECTOR_OPTS)
            .withFaceLandmarks()
            .withFaceDescriptor();
        } catch (e) {
          console.error('[kiosk] detection failed:', e);
          return;
        }
        if (!detection) { streak.current = { userId: null, count: 0 }; return; }

        const m = bestMatch(detection.descriptor, people, thresholds);

        if (m.ambiguous) {
          streak.current = { userId: null, count: 0 };
          setStatus('Hold on — look straight at the camera');
          setDetail(`Could be ${m.user.name} or ${m.runnerUp.name} — not marking either`);
          return;
        }
        if (!m.matched) {
          streak.current = { userId: null, count: 0 };
          setStatus('Face not recognized — please see HR');
          setDetail('');
          return;
        }

        // Same person on consecutive scans, or start counting again.
        if (streak.current.userId === m.user.userId) streak.current.count += 1;
        else streak.current = { userId: m.user.userId, count: 1 };
        if (streak.current.count < CONSECUTIVE_HITS) {
          setStatus(`Hold still, ${m.user.name}…`);
          setDetail('');
          return;
        }
        streak.current = { userId: null, count: 0 };

        const now = Date.now();
        const lastForUser = lastLocalMark.current[m.user.userId];
        if (lastForUser && now - lastForUser < LOCAL_COOLDOWN_MS) return;
        lastLocalMark.current[m.user.userId] = now;
        lastAnyMark.current = now;

        try {
          const { data } = await api.post('/attendance/kiosk/mark', {
            userId: m.user.userId,
            confidence: 1 - m.distance,
            distance: m.distance,
            margin: Number.isFinite(m.margin) ? m.margin : null,
          });
          setStatus(`✅ ${data.userName} — Checked ${String(data.status).toUpperCase()} at ${new Date(data.time).toLocaleTimeString()}`);
          setDetail('');
        } catch (e) {
          setStatus(e.response?.data?.error || 'Could not mark attendance');
          setDetail('');
        }
        setTimeout(() => { setStatus('Scanning…'); setDetail(''); }, 3000);
      } finally {
        scanning.current = false;
      }
    }, SCAN_INTERVAL_MS);
    return () => clearInterval(scanTimer);
  }, [people, thresholds, fatal]);

  return (
    <div style={{
      width: '100vw', height: '100vh', background: '#111', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      {!fatal && (
        <video ref={videoRef} autoPlay muted playsInline
          style={{ width: '80vw', maxWidth: 640, borderRadius: 16, background: '#000' }} />
      )}
      <div style={{
        color: fatal ? '#ff8a80' : '#fff', fontSize: fatal ? 22 : 28,
        marginTop: 24, textAlign: 'center', maxWidth: 720, lineHeight: 1.4,
      }}>
        {fatal || status}
      </div>
      {!fatal && detail && (
        <div style={{ color: '#ffb74d', fontSize: 16, marginTop: 8, textAlign: 'center', maxWidth: 720 }}>
          {detail}
        </div>
      )}
      {!fatal && (
        <div style={{ color: '#888', fontSize: 14, marginTop: 16 }}>
          {people.length} employee{people.length === 1 ? '' : 's'} enrolled
        </div>
      )}
    </div>
  );
}
